import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { Contact, CreateContactInput } from '@catalyst-psa/shared';
import { portalRoleFlagLabel } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import {
  ContactFields,
  EMPTY_CONTACT,
  contactFormPayload,
  toContactFormState,
  type ContactFormState,
} from './contactForm.js';
import { useContacts, useCreateContact, useDeleteContact, useUpdateContact } from './api.js';
import { MailerliteStatusBadge } from './MailerliteStatusBadge.js';

/** Pause after the last keystroke before an edit is written. */
const AUTOSAVE_DELAY_MS = 800;

interface ContactsSectionProps {
  customerId: string;
  canEdit: boolean;
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

/** Autosave holds off until the server-required fields would pass validation. */
function isSavable(payload: Omit<CreateContactInput, 'customerId'>): boolean {
  return (
    payload.firstName.length > 0 &&
    payload.lastName.length > 0 &&
    /^\S+@\S+\.\S+$/.test(payload.email)
  );
}

/** Edits to an existing contact save themselves; a new contact still needs the button. */
function SaveStateBadge({
  isNew,
  dirty,
  autosave,
}: {
  isNew: boolean;
  dirty: boolean;
  autosave: 'idle' | 'saving' | 'saved';
}): JSX.Element {
  if (isNew) return <Badge tone="red">Contact not saved</Badge>;
  if (autosave === 'saving') return <Badge tone="amber">Saving…</Badge>;
  if (dirty) return <Badge tone="red">Unsaved changes</Badge>;
  if (autosave === 'saved') return <Badge tone="green">All changes saved</Badge>;
  return <Badge tone="gray">No changes</Badge>;
}

/** The role flags the Catalyst Portal currently has switched on for this contact. */
export function PortalFlagBadges({ contact }: { contact: Contact }): JSX.Element {
  const on = Object.keys(contact.portalRoleFlags)
    .filter((key) => contact.portalRoleFlags[key])
    .sort();
  const known = contact.portalUserId !== null || Object.keys(contact.portalRoleFlags).length > 0;
  if (!known) return <span className="text-slate-400">—</span>;

  return (
    <div className="flex flex-wrap gap-1">
      <Badge tone={contact.portalStatus === 'active' ? 'green' : 'gray'}>
        {contact.portalStatus ?? 'unknown'}
      </Badge>
      {on.map((key) => (
        <Badge key={key} tone="blue">
          {portalRoleFlagLabel(key)}
        </Badge>
      ))}
    </div>
  );
}

export function notificationCategoryNames(
  contact: Contact,
  nameById: ReadonlyMap<string, string>,
): string {
  return (
    contact.notificationServiceCategoryIds
      .map((id) => nameById.get(id))
      .filter((name): name is string => Boolean(name))
      .join(', ') || '—'
  );
}

export function ContactsSection({ customerId, canEdit }: ContactsSectionProps): JSX.Element {
  // `null` = closed, `'new'` = create form, otherwise the contact id being edited.
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<ContactFormState>(EMPTY_CONTACT);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce the search box so each keystroke doesn't fire a request.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const contactsQuery = useContacts({ customerId, search: search || undefined });
  const serviceCategoriesQuery = useServiceCategoryOptions();
  const serviceCategoryOptions = serviceCategoriesQuery.data ?? [];
  const categoryNameById = useMemo(
    () => new Map(serviceCategoryOptions.map((c) => [c.id, c.name])),
    [serviceCategoryOptions],
  );
  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();
  const deleteMutation = useDeleteContact();
  const { mutate: updateContact } = updateMutation;

  const contacts = contactsQuery.data?.data ?? [];
  const isFormOpen = editing !== null;
  const isEdit = isFormOpen && editing !== 'new';
  const mutation = editing === 'new' ? createMutation : updateMutation;
  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError = mutation.isError
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : 'Failed to save contact.'
    : null;

  // Serialized copy of what the server last accepted, so autosave can tell what changed.
  const savedPayloadRef = useRef('');
  const [autosave, setAutosave] = useState<'idle' | 'saving' | 'saved'>('idle');
  const payload = contactFormPayload(form);
  const serialized = JSON.stringify(payload);
  const dirty = isEdit && serialized !== savedPayloadRef.current;

  useEffect(() => {
    if (!isEdit || !editing || !dirty || !isSavable(payload)) return;
    const timer = setTimeout(() => {
      setAutosave('saving');
      updateContact(
        { id: editing, input: payload },
        {
          onSuccess: () => {
            savedPayloadRef.current = serialized;
            setAutosave('saved');
          },
          onError: () => setAutosave('idle'),
        },
      );
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [dirty, editing, isEdit, payload, serialized, updateContact]);

  const openCreate = (): void => {
    setForm(EMPTY_CONTACT);
    setEditing('new');
    savedPayloadRef.current = '';
    setAutosave('idle');
    createMutation.reset();
    updateMutation.reset();
  };

  const openEdit = (contact: Contact): void => {
    const next = toContactFormState(contact);
    setForm(next);
    setEditing(contact.id);
    savedPayloadRef.current = JSON.stringify(contactFormPayload(next));
    setAutosave('idle');
    createMutation.reset();
    updateMutation.reset();
  };

  const closeForm = (): void => {
    setEditing(null);
    setConfirmOpen(false);
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const base = contactFormPayload(form);

    if (editing === 'new') {
      const input: CreateContactInput = { customerId, ...base };
      createMutation.mutate(input, { onSuccess: closeForm });
    } else if (editing) {
      updateMutation.mutate({ id: editing, input: base }, { onSuccess: closeForm });
    }
  };

  const handleDelete = (): void => {
    if (!isEdit || !editing) {
      return;
    }
    deleteMutation.mutate(editing, { onSuccess: closeForm });
  };

  const notificationNames = (contact: Contact): string =>
    notificationCategoryNames(contact, categoryNameById);

  const renderForm = (): JSX.Element => (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 border-y border-rose-300 bg-rose-50/70 p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800">
          {editing === 'new' ? 'New contact' : 'Edit contact'}
        </span>
        <div className="flex items-center gap-2">
          <SaveStateBadge isNew={editing === 'new'} dirty={dirty} autosave={autosave} />
          <Button
            type="button"
            variant="secondary"
            onClick={closeForm}
            disabled={mutation.isPending}
            className="px-2.5 py-1 text-xs"
          >
            {isEdit ? 'Close' : 'Cancel'}
          </Button>
          <Button type="submit" isLoading={mutation.isPending} className="px-2.5 py-1 text-xs">
            {editing === 'new' ? 'Add contact' : 'Save contact'}
          </Button>
        </div>
      </div>
      {generalError ? (
        <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{generalError}</div>
      ) : null}
      <ContactFields
        form={form}
        setForm={setForm}
        fieldErrors={fieldErrors}
        contact={contacts.find((c) => c.id === editing) ?? null}
      />
      <div className="flex items-center gap-2">
        {isEdit ? (
          <Button
            type="button"
            variant="ghost"
            className="mr-auto text-rose-600 hover:bg-rose-50"
            onClick={() => setConfirmOpen(true)}
            disabled={mutation.isPending || deleteMutation.isPending}
          >
            Delete
          </Button>
        ) : null}
        <Button type="button" variant="secondary" onClick={closeForm} disabled={mutation.isPending}>
          {isEdit ? 'Close' : 'Cancel'}
        </Button>
        <SaveStateBadge isNew={editing === 'new'} dirty={dirty} autosave={autosave} />
        <Button type="submit" isLoading={mutation.isPending}>
          {editing === 'new' ? 'Add contact' : 'Save contact'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <span className="text-sm font-medium text-slate-700">Contacts</span>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email, title…"
            className="w-52 rounded-lg border border-slate-300 px-2.5 py-1 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          {canEdit ? (
            <Button
              variant="secondary"
              onClick={openCreate}
              disabled={editing === 'new'}
              className="px-2.5 py-1 text-xs"
            >
              Add contact
            </Button>
          ) : null}
        </div>
      </div>

      {editing === 'new' ? renderForm() : null}

      {contactsQuery.isLoading ? (
        <p className="px-3 py-2 text-sm text-slate-500">Loading contacts…</p>
      ) : contacts.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-500">
          {search ? 'No contacts match your search.' : 'No contacts yet.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Title</th>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Notifications for</th>
                <th className="px-3 py-2 font-medium">Billing</th>
                <th className="px-3 py-2 font-medium">Cat+</th>
                <th className="px-3 py-2 font-medium">MailerLite</th>
                <th className="px-3 py-2 font-medium">Catalyst Portal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {contacts.map((contact) => (
                <Fragment key={contact.id}>
                  <tr
                    className={canEdit ? 'cursor-pointer align-top hover:bg-slate-50' : 'align-top'}
                    onClick={canEdit ? () => openEdit(contact) : undefined}
                  >
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium text-slate-900">
                          {`${contact.firstName} ${contact.lastName ?? ''}`.trim()}
                        </span>
                        {contact.isPrimary ? <Badge tone="blue">Primary</Badge> : null}
                        {!contact.isActive ? <Badge tone="gray">Inactive</Badge> : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{contact.title || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{contact.email || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{notificationNames(contact)}</td>
                    <td className="px-3 py-2">
                      {contact.billingContact ? <Badge tone="blue">Billing</Badge> : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {contact.catPlusNotifications ? <Badge tone="green">Cat+</Badge> : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <MailerliteStatusBadge contact={contact} />
                    </td>
                    <td className="px-3 py-2">
                      <PortalFlagBadges contact={contact} />
                    </td>
                  </tr>
                  {editing === contact.id ? (
                    <tr>
                      <td colSpan={8} className="p-0">
                        {renderForm()}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete contact"
        message={`Delete "${`${form.firstName} ${form.lastName}`.trim()}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
